import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import subscriptionService from '../../services/subscriptionService';

const BottomTabNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, loading: authLoading, userProfile, user, profileLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  // Check if user is a MyBodyToday officer (based on email domain)
  const isOfficer = user?.email?.toLowerCase()?.includes('@mybodytoday.') ||
    user?.email?.toLowerCase()?.includes('@mybodytoday1.') ||
    user?.email?.toLowerCase() === 'owner@mybodytoday.com' ||
    user?.email?.toLowerCase() === 'admin@mybodytoday.com' ||
    user?.email?.toLowerCase() === 'owner@mybodytoday1.com' ||
    user?.email?.toLowerCase() === 'admin@mybodytoday1.com';

  const tabs = [
    {
      label: 'Today',
      path: '/daily-check-in',
      icon: 'Calendar',
      ariaLabel: 'Navigate to daily check-in'
    },
    {
      label: 'History',
      path: '/symptom-history',
      icon: 'History',
      ariaLabel: 'Navigate to symptom history'
    },
    {
      label: 'Appts',
      path: '/doctor-appointments',
      icon: 'Stethoscope',
      ariaLabel: 'Navigate to doctor appointments'
    },
    {
      label: 'Refer',
      path: '/referral-program',
      icon: 'Gift',
      ariaLabel: 'Navigate to referral program'
    },
    {
      label: 'Export',
      path: '/doctor-summary-export',
      icon: 'FileText',
      ariaLabel: 'Navigate to doctor summary export'
    },
    {
      label: 'Settings',
      path: '/settings-and-privacy',
      icon: 'Settings',
      ariaLabel: 'Navigate to settings and privacy'
    }
  ];

  // Add Caregiver tabs if user is a caregiver
  const caregiverTab = {
    label: 'Caregiver',
    path: '/caregiver-dashboard',
    icon: 'Users',
    ariaLabel: 'Navigate to caregiver dashboard'
  };

  const analyticsTab = {
    label: 'Analytics',
    path: '/caregiver-analytics-dashboard',
    icon: 'BarChart3',
    ariaLabel: 'Navigate to analytics dashboard'
  };

  const criticalAlertsTab = {
    label: 'Alerts',
    path: '/critical-alerts-dashboard',
    icon: 'AlertTriangle',
    ariaLabel: 'Navigate to critical alerts dashboard'
  };

  const businessIntelligenceTab = {
    label: 'BI',
    path: '/business-intelligence-dashboard',
    icon: 'TrendingUp',
    ariaLabel: 'Navigate to business intelligence dashboard'
  };

  const masterDashboardTab = {
    label: 'Master',
    path: '/my-body-today-officers-master-dashboard',
    icon: 'LayoutDashboard',
    ariaLabel: 'Navigate to officers master dashboard'
  };

  const engagementAnalyticsTab = {
    label: 'Engagement',
    path: '/user-engagement-analytics',
    icon: 'Activity',
    ariaLabel: 'Navigate to engagement analytics'
  };

  // Determine which tabs to display based on user type and officer status
  const displayTabs = React.useMemo(() => {
    // Wait for profile to load before determining tabs
    if (profileLoading || !userProfile) {
      // Return default tabs while loading to prevent layout shift
      return tabs;
    }

    // Officers get special navigation with Master Dashboard
    if (isOfficer) {
      return [
        masterDashboardTab,
        caregiverTab,
        // analyticsTab, // Temporarily disabled - showing zeros
        // criticalAlertsTab, // Temporarily disabled - problematic
        businessIntelligenceTab,
        {
          label: 'Settings',
          path: '/settings-and-privacy',
          icon: 'Settings',
          ariaLabel: 'Navigate to settings and privacy'
        }
      ];
    }
    // Caregivers (non-officers) get caregiver-specific tabs without Master Dashboard
    if (userProfile?.user_type === 'caregiver') {
      return [
        caregiverTab,
        analyticsTab,
        criticalAlertsTab,
        businessIntelligenceTab,
        {
          label: 'Settings',
          path: '/settings-and-privacy',
          icon: 'Settings',
          ariaLabel: 'Navigate to settings and privacy'
        }
      ];
    }
    // Regular users get standard tabs
    return tabs;
  }, [isOfficer, userProfile?.user_type, profileLoading, userProfile]);

  const navigationItems = [
    { 
      path: '/caregiver-dashboard', 
      icon: 'Home', 
      label: 'Dashboard',
      roles: ['caregiver']
    },
    { 
      path: '/user-relationship-management', 
      icon: 'Users', 
      label: 'Dependents',
      roles: ['caregiver']
    },
    { 
      path: '/caregiver-analytics-dashboard', 
      icon: 'BarChart3', 
      label: 'Analytics',
      roles: ['caregiver']
    },
    { 
      path: '/master-analytics-dashboard', 
      icon: 'BarChart', 
      label: 'Master',
      roles: ['caregiver']
    },
    { 
      path: '/critical-alerts-dashboard', 
      icon: 'AlertTriangle', 
      label: 'Alerts',
      roles: ['caregiver']
    },
    { 
      path: '/business-intelligence-dashboard', 
      icon: 'TrendingUp', 
      label: 'BI',
      roles: ['caregiver']
    },
    { 
      path: '/user-engagement-analytics', 
      icon: 'Activity', 
      label: 'Engagement',
      roles: ['caregiver']
    },
    { 
      path: '/premium-conversion-funnel-analytics', 
      icon: 'Target', 
      label: 'Conversion',
      roles: ['caregiver']
    },
    { 
      path: '/audit-trail-dashboard', 
      icon: 'Shield', 
      label: 'Audit',
      roles: ['caregiver']
    },
    { 
      path: '/notification-management', 
      icon: 'Bell', 
      label: 'Alerts',
      roles: ['caregiver']
    },
    { 
      path: '/settings-and-privacy', 
      icon: 'Settings', 
      label: 'Settings',
      roles: ['caregiver', 'general_adult', 'elderly_adult', 'long_term_condition', 'child_teen']
    },
    { 
      path: '/daily-check-in', 
      icon: 'Plus', 
      label: 'Check-in',
      roles: ['general_adult', 'elderly_adult', 'long_term_condition', 'child_teen']
    },
    { 
      path: '/symptom-history', 
      icon: 'Clock', 
      label: 'History',
      roles: ['general_adult', 'elderly_adult', 'long_term_condition', 'child_teen']
    },
    { 
      path: '/doctor-summary-export', 
      icon: 'FileText', 
      label: 'Export',
      roles: ['general_adult', 'elderly_adult', 'long_term_condition', 'child_teen']
    }
  ];

  useEffect(() => {
    const currentPath = location?.pathname;
    const currentTab = tabs?.find(tab => tab?.path === currentPath);
    if (currentTab) {
      setActiveTab(currentTab?.path);
      localStorage.setItem('activeTab', currentTab?.path);
    }
  }, [location?.pathname]);

  useEffect(() => {
    const savedTab = localStorage.getItem('activeTab');
    if (savedTab) {
      setActiveTab(savedTab);
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      subscriptionService?.isPremiumPlan()?.then(result => setIsPremium(result))?.catch(() => setIsPremium(false));
    }
  }, [user?.id]);

  const handleTabClick = (tab) => {
    // If user is not premium and taps Appts tab, redirect to upgrade prompt
    if (tab?.path === '/doctor-appointments' && !isPremium) {
      navigate('/appointments-upgrade-prompt');
      return;
    }
    setActiveTab(tab?.path);
    localStorage.setItem('activeTab', tab?.path);
    navigate(tab?.path);
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    
    setLoggingOut(true);
    try {
      const { error } = await signOut();
      if (!error) {
        navigate('/sign-in');
      } else {
        console.error('Logout error:', error);
      }
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-therapeutic-lg z-[100]"
      role="navigation"
      aria-label="Bottom tab navigation"
    >
      <div className="flex items-center justify-between gap-0 px-1 h-16 sm:h-18 md:h-20">
        {displayTabs?.map((tab) => {
          const isActive = activeTab === tab?.path;
          return (
            <button
              key={tab?.path}
              onClick={() => handleTabClick(tab)}
              className={`
                flex flex-col items-center justify-center gap-1 flex-1
                min-h-[48px] px-1 py-2
                rounded-lg transition-smooth touch-manipulation
                ${
                  isActive 
                    ? 'text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }
                focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
                active:scale-95
              `}
              aria-label={tab?.ariaLabel}
              aria-current={isActive ? 'page' : undefined}
            >
              <div className="relative">
                <Icon 
                  name={tab?.icon} 
                  size={20} 
                  strokeWidth={isActive ? 2.5 : 2}
                  className="transition-smooth"
                />
                {tab?.path === '/doctor-appointments' && !isPremium && (
                  <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-amber-400 rounded-full flex items-center justify-center">
                    <Icon name="Lock" size={8} strokeWidth={2.5} className="text-white" />
                  </div>
                )}
              </div>
              <span className={`
                text-[9px] sm:text-[10px] font-medium transition-smooth whitespace-nowrap
                ${isActive ? 'font-semibold' : ''}
              `}>
                {tab?.label}
              </span>
              {isActive && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-1 bg-primary rounded-t-full" />
              )}
            </button>
          );
        })}
        
        {/* Logout Button - Always visible at far right with red styling */}
        {user && (
          <button
            onClick={handleLogout}
            disabled={loggingOut || authLoading}
            className="
              flex flex-col items-center justify-center gap-1 flex-1
              min-h-[48px] px-1 py-2
              rounded-lg transition-smooth touch-manipulation
              text-red-600 hover:text-red-700 hover:bg-red-50
              focus:outline-none focus:ring-3 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-background
              active:scale-95
              disabled:opacity-50 disabled:cursor-not-allowed
            "
            aria-label="Sign out"
          >
            <Icon 
              name={loggingOut ? 'Loader2' : 'LogOut'} 
              size={20} 
              strokeWidth={2}
              className={`transition-smooth ${loggingOut ? 'animate-spin' : ''}`}
            />
            <span className="text-[9px] sm:text-[10px] font-medium transition-smooth whitespace-nowrap">
              {loggingOut ? 'Signing Out...' : 'Logout'}
            </span>
          </button>
        )}
      </div>
    </nav>
  );
};

export default BottomTabNavigation;